/**
 * unitRegistry — module-level map of mounted editable units.
 *
 * Each editable rjbuild unit (a block, an object instance, a fragment, or any
 * subroot bound to a Syncable Object) mounts a `BuilderUnit` primitive that
 * marks its DOM root with `data-builder-unit="<unitId>"` and registers here.
 *
 * The edit runtime can't write into a subroot's private store from the React
 * layer (no imperative handle on ReactiveJsonRoot). So `BuilderUnit` — which
 * lives *inside* the subroot — captures that store's `updateData` and exposes a
 * `setFieldValue(field, value)` closure through this registry. On a field
 * click, the runtime walks the DOM to the nearest `[data-builder-unit]`, looks
 * the unit up here, and commits through *that* unit's closure → the right
 * store, the right DataSync.
 *
 * `hasSubmissionUrl` carries the permission gate: an entry whose owning
 * Syncable Object has no `submission_url` is read-only (Drupal denied write).
 */

const units = new Map()

/**
 * Register a mounted unit. Returns an unregister function (call on unmount).
 *
 * The unregister is identity-guarded: it only removes the entry if it's still
 * the one we registered. This prevents a late unmount (React reconciliation /
 * StrictMode double-invoke) from deleting a newer registration under the same
 * unitId.
 *
 * @param {string} unitId
 * @param {{hasSubmissionUrl: boolean, builderId?: string, setFieldValue: (field: string, value: *) => void, getData?: () => *}} entry
 * @returns {() => void} unregister
 */
export const registerUnit = (unitId, entry) => {
  units.set(unitId, entry)
  return () => {
    if (units.get(unitId) === entry) {
      units.delete(unitId)
    }
  }
}

/** Look up a registered unit by id, or null. */
export const getUnit = (unitId) => units.get(unitId) ?? null

/** Number of currently-registered units (mostly for assertions/debug). */
export const unitCount = () => units.size

/** Clear all units — test helper only. */
export const __clearUnits = () => units.clear()

/**
 * Clés de filtre PARTAGÉ actuellement pilotées par l'URL, par unité.
 *
 * Publié par `BuilderBlockMount` (seul à connaître à la fois le `sharedFilters:`
 * de la déf et la query string), lu par la récolte de `useBuilderSave`. Sans ce
 * pont, un enregistrement fait pendant qu'un filtre de tableau de bord est posé
 * RECOPIE la valeur du filtre dans les réglages propres du widget : le filtre
 * devient son défaut, et le vider ne rend plus le périmètre complet.
 */
const sharedLocks = new Map()

/** Déclare (ou retire, avec une liste vide) les clés pilotées par l'URL. */
export const setSharedFilterLock = (builderId, keys) => {
  if (!builderId) return
  if (keys && keys.length) sharedLocks.set(builderId, keys)
  else sharedLocks.delete(builderId)
}

/** Les clés qu'un save ne doit PAS récolter pour cette unité. */
export const getSharedFilterLock = (builderId) => sharedLocks.get(builderId) ?? []

/** Vide le registre des verrous — utilitaire de test. */
export const __clearSharedLocks = () => sharedLocks.clear()

/**
 * Le contenu VIVANT d'une unité persistée par l'état du canevas.
 *
 * Une unité sans `submission_url` (tous les blocs d'un tableau de bord — dont
 * les blocs ne sont pas des nœuds mais un blob `field_layout` —, un bloc de
 * page pas encore créé) a sa DataSync inerte : c'est le save du canevas qui
 * l'enregistre, depuis `bloc.data`. Or un champ de formulaire du bloc (les
 * filtres d'un widget : filiale, pipeline, période…) écrit dans le STORE de
 * l'unité, jamais dans `bloc.data` — aucun clic d'édition ne passe par
 * `commitField`. Sans cette lecture, la sélection reste en mémoire et le save
 * réécrit l'ancienne valeur.
 *
 * Renvoie l'objet de données de l'unité montée pour ce `builderId`, ou `null`
 * (aucune unité montée, unité auto-persistée, données non exploitables).
 *
 * @param {string} builderId
 * @returns {object|null}
 */
export const getPageStateUnitData = (builderId) => {
  if (!builderId) return null
  for (const entry of units.values()) {
    if (entry.builderId !== builderId || entry.hasSubmissionUrl || !entry.getData) continue
    const data = entry.getData()
    if (data && typeof data === 'object' && !Array.isArray(data)) return data
  }
  return null
}
